import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import axios from "axios";
import { Link } from "react-router-dom";

import {
  Button,
  PageFilterBar,
  ScopeSelect,
  SearchInput,
  StatePanel,
  Toggle,
} from "../../components/ui";

import BuyTargetSummary from "../components/BuyTargetSummary.jsx";
import CoreListCard from "../components/CoreListCard.jsx";
import BuyTargetPanel from "../components/BuyTargetPanel.jsx";
import { FILTERS } from "../components/CoreListFilterPills.jsx";

const matchesSearch = (ref, brand, model, query) => {
  if (!query) return true;
  const q = query.toLowerCase();
  return (
    (ref.reference || "").toLowerCase().includes(q) ||
    (brand || "").toLowerCase().includes(q) ||
    (model || "").toLowerCase().includes(q)
  );
};

/**
 * Is anything at this reference's buy target, from ANY source?
 *
 * Trade-group quotes and marketplace listings both count (Alex, 2026-10-08).
 * The two are not equally verified — a trade quote was checked against the
 * target in the store's own currency, while a marketplace listing is the
 * scraper's assertion that it sits under a ceiling we asked for — but for the
 * question "is there something to look at", both are a yes, and counting only
 * one of them left references reading 0 with listings waiting inside.
 */
//
// Live auction lots joined on 2026-11-02 (Stock Alert): a lot whose current
// bid is under the band for its year. Kept in step with useCoreListTargets,
// which counts the KPI tile from the same three fields.
const atTargetCount = (entry) =>
  (Number(entry?.hit_count) || 0) +
  (Number(entry?.marketplace?.match_count) || 0) +
  (Number(entry?.auction?.match_count) || 0);

// "in-demand" is withdrawn with the demand hook (see CoreListTemplate).
const matchesFilter = (ref, filter, targetEntry) => {
  if (filter === "at-target") return atTargetCount(targetEntry) > 0;
  // Out of stock = nothing live. Same rule the toggle uses, with the same
  // fallback for an API that has not shipped the counts yet (see viewOf).
  if (filter === "out-of-stock") {
    if (ref.active_listing_count !== undefined && ref.active_listing_count !== null) {
      return (Number(ref.active_listing_count) || 0) === 0;
    }
    const hasListing = ref.most_recent_listing_id != null;
    const isSold = ref.most_recent_listing_is_sold === true;
    const isActive = ref.most_recent_listing_status === "active";
    return !hasListing || isSold || !isActive;
  }
  return true; // "all"
};

/**
 * F3 — Core List grouped table.
 *
 * Renders { groups: [{brand, models: [{model, references: [...]}]}] } as
 * Brand → Model → Reference rows. Backend controls sort order; we render
 * groups exactly in the order received, then filter client-side by search
 * text and the All/In demand/Out of stock pill.
 *
 * Fontaine M36B — each MODEL group is now one shared `DataTable` (`ft-table`):
 * a 36px compact table at ≥900px, one Card per reference below it. The brand
 * and model headers stay this page's own markup, which is exactly the
 * composition the M36A contract prescribes for a grouped table (the primitive
 * deliberately owns no grouping API). Nothing about the data, the endpoints,
 * the permissions or the row actions changed — see
 * `tests/stock/CoreListTableContract.test.jsx`, which asserts all of them at
 * BOTH widths and was written before this migration.
 *
 * Props:
 *   - groups[]            grouped references (see backend shape)
 *   - isFetching          boolean — gates the empty/no-results verdicts
 *   - initialFilter        "all" | "in-demand" | "out-of-stock" — seeded from
 *                          a legacy ?filter=alerting deep link, if present
 *   - storeIdM            integer store id (used for refetch keying upstream)
 *   - onChanged()         parent refetch trigger (after add/remove/toggle)
 */
const CoreListTable = ({
  groups = [],
  isFetching,
  initialFilter = "all",
  targetsByItemId,
  storeIdM,
  onChanged,
}) => {
  // Two pieces of state on purpose.
  //
  // `searchText` is what is IN the box and updates on every keystroke, so the
  // field is never waiting on anything — the reported "typed text disappears"
  // is impossible when the input's value is its own state.
  //
  // `query` is what actually FILTERS, and only catches up 300ms after typing
  // stops (SearchInput's own debounce, which this page simply was not using).
  // Filtering re-renders every reference in every model group; at 116
  // references that is a heavy synchronous render, and doing it per keystroke
  // is what made the page feel like it was reloading itself.
  const [searchText, setSearchText] = useState("");
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState(initialFilter);


  const counts = useMemo(() => {
    let all = 0;
    let atTarget = 0;
    let outOfStock = 0;
    groups.forEach((b) =>
      (b.models || []).forEach((m) =>
        (m.references || []).forEach((ref) => {
          all += 1;
          const entry = targetsByItemId?.get(ref.core_list_item_id);
          if (atTargetCount(entry) > 0) atTarget += 1;
          if (matchesFilter(ref, "out-of-stock", entry)) outOfStock += 1;
        })
      )
    );
    return {
      all,
      "at-target": atTarget,
      "out-of-stock": outOfStock,
    };
  }, [groups, targetsByItemId]);

  const filteredGroups = useMemo(() => {
    return groups
      .map((brandGroup) => ({
        ...brandGroup,
        models: (brandGroup.models || [])
          .map((modelGroup) => ({
            ...modelGroup,
            references: (modelGroup.references || []).filter((ref) => {
              return (
                matchesSearch(ref, brandGroup.brand, modelGroup.model, query) &&
                matchesFilter(ref, filter, targetsByItemId?.get(ref.core_list_item_id))
              );
            }),
          }))
          .filter((m) => m.references.length > 0),
      }))
      .filter((b) => b.models.length > 0);
  }, [groups, query, filter, targetsByItemId]);

  const hasAnyData = groups.length > 0;
  const noResults = !isFetching && hasAnyData && filteredGroups.length === 0;
  const isEmpty = !isFetching && !hasAnyData;

  return (
    <div className="core-list-table">
      {/* M54D — the shared filter row (PageFilterBar): the same fixed-length
          pill and right-hand dropdown as Buy Alerts, Manage Listings and Leads.
          The segmented All / In demand / Out of stock pills are now one
          ScopeSelect; "all" is the absence of a selection, and the counts ride
          in the option labels exactly as they did on the pills. */}
      <PageFilterBar className="core-list-table__filterbar" label="Filter core list"
        search={(
          <SearchInput
            size="lg"
            placeholder="Search reference, brand or model"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            // Debounced — `setQuery` is a stable setState reference, so the
            // primitive's timer is not rebuilt on every render.
            onSearch={setQuery}
            onClear={() => {
              setSearchText("");
              setQuery("");
            }}
          />
        )}
      >
        <ScopeSelect
          label="Filter core list"
          placeholder={counts.all != null ? `All references ${counts.all}` : "All references"}
          multiple={false}
          options={FILTERS.filter((f) => f.key !== "all").map((f) => ({
            key: f.key,
            label: counts[f.key] != null ? `${f.label} ${counts[f.key]}` : f.label,
          }))}
          value={filter === "all" ? null : filter}
          onChange={(next) => setFilter(next ?? "all")}
        />

        {/* The list/grid switch is gone (Alex, 2026-10-09: "Keep only grid
            view. Remove list view."). There is one presentation now, so there
            is nothing to choose between — and a switch offering one option is
            a control that only ever costs a click. */}
      </PageFilterBar>

      {/* Two distinct verdicts, one state vocabulary. There is no DataTable to
          carry a `state` prop when nothing matched — a grouped table has no
          model groups left to render — so the shared StatePanel (M37, already
          live on this route for the load error) is used directly. The
          no-results copy is unchanged from the hand-rolled
          `.core-list-table__empty` divs these replace; the empty title is the
          shorter M36B wording. */}
      {isEmpty && (
        <StatePanel
          state="empty"
          icon="Package"
          title="Add product to start"
          description={null}
        />
      )}

      {noResults && (
        <StatePanel
          state="no-results"
          title="No references match your search or filter."
          description={null}
        />
      )}

      {filteredGroups.map((brandGroup) => {
        const modelCount = (brandGroup.models || []).length;
        const refCount = (brandGroup.models || []).reduce(
          (sum, m) => sum + (m.references || []).length,
          0
        );

        return (
          <div className="core-list-table__brand-block" key={brandGroup.brand}>
            <div className="core-list-table__brand-header">
              <span className="core-list-table__brand-name">{brandGroup.brand}</span>
              <span className="core-list-table__brand-counts">
                {refCount} {refCount === 1 ? "ref" : "refs"} · {modelCount}{" "}
                {modelCount === 1 ? "model" : "models"}
              </span>
            </div>

            {(brandGroup.models || []).map((modelGroup) => (
              <div
                className="core-list-table__model-block"
                key={`${brandGroup.brand}::${modelGroup.model}`}
              >
                <div className="core-list-table__model-header">{modelGroup.model}</div>

                <CoreListModelTable
                  brand={brandGroup.brand}
                  model={modelGroup.model}
                  references={modelGroup.references || []}
                  targetsByItemId={targetsByItemId}
                  storeIdM={storeIdM}
                  // Only the WHOLE model can be reordered: dragging inside a
                  // searched or filtered subset would number the visible cards
                  // and scramble the hidden ones around them.
                  canReorder={!query && filter === "all"}
                  onChanged={onChanged}
                />
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
};

// The per-row /api/products/{id} fetch that used to live here is GONE.
//
// It cost one request per reference. At 116 references that is 116 requests
// against a route limited to 60/minute per IP (productApiRateLimit), so the
// page 429d itself, starved its own /core-list/targets call, and fell over
// while typing in the search box. It was invisible until the platform-wide
// rate limiter was repaired.
//
// The thumbnail and the spec line now arrive on the core-list row itself
// (image_file_id / case_material / case_diameter, bulk-loaded in two queries
// by CoreListService). Zero extra requests.

/** The listing-derived facts a row renders from, plus any local override. */
/**
 * What a row shows, derived from EVERY listing the reference has rather than
 * only the newest one.
 *
 * `inventory`  how many listings exist for this reference in this store, any
 *              status. 0 means none was ever created.
 * `active`     true when AT LEAST ONE listing is active. Previously this read
 *              the newest listing alone, so a reference with a live listing
 *              plus a newer inactive one showed as Off.
 * `canReuse`   nothing active, but a sold listing exists to start from:
 *              Create then copies it (see onCreate).
 * `canCreate`  no listing at all — Create starts from a blank form.
 */
const viewOf = (row, local) => {
  // The counts come from a backend that may not be deployed yet — this page is
  // routinely run against an API one release behind. When they are absent, fall
  // back to the most_recent_listing_* fields every version has sent.
  //
  // Without this the page reads `undefined` as 0 and reports "no listing,
  // Create" against EVERY reference, which looks like the feature is broken
  // rather than like a field is missing.
  const hasCounts = row.listing_count !== undefined && row.listing_count !== null;

  const hadListing = row.most_recent_listing_id != null;
  const wasSold = row.most_recent_listing_is_sold === true;
  const wasActive = row.most_recent_listing_status === "active";

  const inventory = hasCounts ? Number(row.listing_count) || 0 : hadListing ? 1 : 0;
  const activeCount = hasCounts
    ? Number(row.active_listing_count) || 0
    : hadListing && wasActive && !wasSold
      ? 1
      : 0;
  const soldCount = hasCounts
    ? Number(row.sold_listing_count) || 0
    : hadListing && wasSold
      ? 1
      : 0;

  const anyActive = activeCount > 0;

  // Stock / Consignment / Merchant. Same graceful-degradation rule as the
  // counts above: an API one release behind sends none of these, and a split
  // of three zeros under a non-zero total would read as a contradiction, so
  // `ownership` stays null and the cell shows the total alone.
  const hasOwnership = row.stock_count !== undefined && row.stock_count !== null;
  const ownership = hasOwnership
    ? {
        stock: Number(row.stock_count) || 0,
        consignment: Number(row.consignment_count) || 0,
        merchant: Number(row.merchant_count) || 0,
      }
    : null;

  return {
    inventory,
    ownership,
    activeCount,
    soldCount,
    hasListing: inventory > 0,
    // The optimistic toggle value wins once the user has touched this row.
    active: local.active === undefined ? anyActive : local.active,
    // Create is offered only when nothing is live.
    canReuse: !anyActive && soldCount > 0,
    canCreate: inventory === 0,
    busy: !!local.busy,
    removing: !!local.removing,
  };
};

/**
 * The Stock / Consignment / Merchant breakdown under the inventory count.
 *
 * One number cannot distinguish eight watches we own from eight a merchant
 * owns, and those are different businesses — the first is capital we have
 * tied up, the second is not ours at all. Alex asked for the split on
 * 2026-10-06.
 *
 * Empty buckets are dropped rather than shown as zeros: on a page of 116
 * references most rows use one or two buckets, and three figures per row
 * where one would do is what made the old pill vocabulary unreadable.
 * `to_be_procured` ("Owned By") is folded into Stock server-side.
 */
const OWNERSHIP_LABELS = [
  ["stock", "Stock", "S"],
  ["consignment", "Consignment", "C"],
  ["merchant", "Merchant", "M"],
];

const OwnershipSplit = ({ ownership }) => {
  const parts = OWNERSHIP_LABELS.filter(([key]) => ownership[key] > 0);

  if (!parts.length) return null;

  return (
    <span
      className="core-list-table__ownership"
      title={parts
        .map(([key, label]) => `${ownership[key]} ${label.toLowerCase()}`)
        .join(" · ")}
    >
      {parts.map(([key, label, initial]) => (
        <span
          key={key}
          className={`core-list-table__ownership-part is-${key}`}
          aria-label={`${ownership[key]} ${label.toLowerCase()}`}
        >
          {ownership[key]}
          <abbr title={label}>{initial}</abbr>
        </span>
      ))}
    </span>
  );
};

/**
 * ONE model group — one `DataTable`. Table at ≥900px, one Card per reference
 * below it, from a single set of column definitions.
 */
/**
 * One card cell that can be dragged within its model's grid. The whole card is
 * the handle; a mouse drag only starts after 8px of movement, so a click still
 * opens the card and still presses its buttons.
 */
const SortableCell = ({ id, open, disabled, children }) => {
  const { setNodeRef, attributes, listeners, transform, transition, isDragging } =
    useSortable({ id, disabled });

  // dnd-kit's default role="button" would nest the card's own buttons inside
  // a button; the cell keeps its list-item role and stays focusable for the
  // keyboard sensor (space to lift, arrows to move).
  // eslint-disable-next-line no-unused-vars
  const { role, ...sortableAttributes } = attributes;

  return (
    <li
      ref={setNodeRef}
      className={`cl-grid__cell${open ? " is-open" : ""}${isDragging ? " is-dragging" : ""}`}
      style={{
        transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
        transition,
      }}
      {...(disabled ? {} : sortableAttributes)}
      {...(disabled ? {} : listeners)}
    >
      {children}
    </li>
  );
};

const CoreListModelTable = ({
  brand,
  model,
  references,
  targetsByItemId,
  storeIdM,
  canReorder = false,
  onChanged,
}) => {
  // Drag-and-drop order within this model, shared by the whole store
  // (Guillaume, 2026-10-12). Held locally so a drop shows at once, and reset
  // only when the SERVER's order changes — keyed on the id sequence, not the
  // array, so a targets refetch rebuilding `references` cannot snap a card
  // back mid-save.
  const serverOrder = references.map((r) => r.core_list_item_id).join(",");
  const [order, setOrder] = useState(() => references.map((r) => r.core_list_item_id));
  useEffect(() => {
    setOrder(serverOrder ? serverOrder.split(",").map(Number) : []);
  }, [serverOrder]);

  const byId = useMemo(
    () => new Map(references.map((r) => [r.core_list_item_id, r])),
    [references]
  );
  const ordered = order.map((id) => byId.get(id)).filter(Boolean);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 300, tolerance: 10 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  // Which reference has its buy-target section open, by core_list_item_id.
  //
  // One at a time, rendered directly beneath its OWN row via DataTable's
  // opt-in `renderRowDetail`. It previously sat after the whole model group,
  // which read as belonging to the last reference rather than the one clicked.
  // The panel itself stays this page's own markup; the primitive only places it.
  const [openItemId, setOpenItemId] = useState(null);

  // Which reference was opened ON its matches rather than on the target form.
  // The panel owns the tab from there; this only decides which one it lands
  // on. Cleared when a card is collapsed, so reopening it from the target
  // button starts on the form.
  const [matchItemId, setMatchItemId] = useState(null);

  const toggleTargets = useCallback((itemId) => {
    setOpenItemId((current) => (current === itemId ? null : itemId));
    setMatchItemId(null);
  }, []);

  // The card's "945 at target" ribbon lands on the Matches tab, skipping the
  // form: someone clicking the count wants the quotes, not the editor.
  const openMatches = useCallback((itemId) => {
    setOpenItemId(itemId);
    setMatchItemId(itemId);
  }, []);

  // The mouseup that ends a drag also fires a click on the card under it,
  // which would open it. Clicks within a moment of a drop are swallowed.
  const droppedAt = useRef(0);
  const swallowClickAfterDrop = (e) => {
    if (Date.now() - droppedAt.current < 300) {
      e.stopPropagation();
      e.preventDefault();
    }
  };

  const onDragEnd = useCallback(
    async ({ active, over }) => {
      droppedAt.current = Date.now();
      if (!over || active.id === over.id) return;
      const previous = order;
      const next = arrayMove(order, order.indexOf(active.id), order.indexOf(over.id));
      setOrder(next);
      try {
        await axios.put("/api/core-list/reorder", { store_id_m: storeIdM, item_ids: next });
        onChanged && onChanged();
      } catch (e) {
        setOrder(previous);
        console.error("Failed to save the Core List order", e);
        alert("Couldn't save the new order. Please try again.");
      }
    },
    [order, storeIdM, onChanged]
  );

  // Per-row UI state, keyed by core_list_item_id. This is the state the old
  // `CoreListTableRow` held in three `useState`s; hoisting it here is what
  // lets the rows be plain data for the primitive. Keyed (not indexed) so a
  // refetch that reorders or filters rows can never mis-apply it.
  const [rowState, setRowState] = useState({});
  const patchRow = useCallback((id, patch) => {
    setRowState((current) => ({ ...current, [id]: { ...current[id], ...patch } }));
  }, []);

  const onToggleActive = useCallback(
    async (row, val) => {
      if (!row.most_recent_listing_id || row.most_recent_listing_is_sold === true) return;
      const id = row.core_list_item_id;
      patchRow(id, { busy: true, active: val });
      try {
        await axios.put(`/api/listings/${row.most_recent_listing_id}/active`, {
          active: val,
        });
        onChanged && onChanged();
      } catch (e) {
        // Revert on failure.
        patchRow(id, { active: !val });
        console.error("Failed to toggle listing active", e);
      } finally {
        patchRow(id, { busy: false });
      }
    },
    [onChanged, patchRow]
  );

  const onCreate = useCallback(async (row) => {
    // Nothing sold to start from: the create-listing flow, pre-filled with
    // this product, in a new tab.
    if (row.most_recent_listing_is_sold !== true) {
      window.open(`/product/create/${row.product_id}`, "_blank", "noopener,noreferrer");
      return;
    }
    // Sold: copy the sold listing as an inactive draft, minus everything
    // internal (buying price, supplier, serial, costs, notes; see
    // ListingService::INTERNAL_COLUMNS), and open its edit page. Same
    // endpoint as Manage Listings' duplicate, which keeps those fields.
    // ponytail: the tab opens after the request; browsers allow that within
    // their ~5s user-activation window. Pre-open a blank tab if it is ever blocked.
    try {
      const { data } = await axios.post(
        `/api/listings/${row.most_recent_listing_id}/duplicate`,
        { strip_internal: true }
      );
      window.open(`/product/${data.id}/edit/product`, "_blank", "noopener,noreferrer");
      onChanged && onChanged();
    } catch (e) {
      console.error("Failed to duplicate the sold listing", e);
      alert("Couldn't create a listing from the sold one. Please try again.");
    }
  }, [onChanged]);

  const onRemove = useCallback(
    async (row) => {
      const confirmed = window.confirm(
        `Remove ${row.reference || "this reference"} from the Core List?`
      );
      if (!confirmed) return;
      const id = row.core_list_item_id;
      patchRow(id, { removing: true });
      try {
        await axios.delete(`/api/core-list/${id}`);
        onChanged && onChanged();
      } catch (e) {
        console.error("Failed to remove from Core List", e);
        alert("Failed to remove. Please try again.");
        patchRow(id, { removing: false });
      }
    },
    [onChanged, patchRow]
  );


  // A GRID OF CARDS, not a table (Alex, 2026-10-09). The open reference's
  // panel is a grid item spanning every column, placed straight after its own
  // card, so it appears under the card you clicked and pushes the rest to the
  // next row - the same adjacency the table's row-detail gave, without a table.
  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      // The open panel is a full-width grid item between cards; closing it
      // first keeps the drop targets where the cards actually are.
      onDragStart={() => setOpenItemId(null)}
      onDragEnd={onDragEnd}
      onDragCancel={() => { droppedAt.current = Date.now(); }}
    >
    <SortableContext items={order} strategy={rectSortingStrategy} disabled={!canReorder}>
    <ul
      className="cl-grid"
      aria-label={`${brand} ${model} core references`}
      onClickCapture={swallowClickAfterDrop}
    >
      {ordered.map((row) => {
        const id = row.core_list_item_id;
        const open = openItemId === id;

        return (
          <React.Fragment key={id}>
            <SortableCell id={id} open={open} disabled={!canReorder}>
              <CoreListCard
                row={row}
                brand={brand}
                model={model}
                view={viewOf(row, rowState[id] || {})}
                entry={targetsByItemId?.get(id)}
                isOpen={open}
                onToggleTargets={toggleTargets}
                onOpenMatches={openMatches}
                onToggleActive={onToggleActive}
                onCreate={onCreate}
                onRemove={onRemove}
              />
            </SortableCell>

            {/* The detail slot spans every grid column, so the match tab gets
                the full page width that a row of feed cards needs. Setting a
                target and reading the matches are two TABS inside it (Alex,
                2026-10-12), not two screens: you move between them constantly
                — set a ceiling, see what it caught, move the ceiling. */}
            {open && (
              <li className="cl-grid__detail">
                <div className="core-list-table__target-block">
                  <div className="core-list-table__target-block-header">
                    <span className="core-list-table__target-block-ref">
                      {brand} {row.reference || model}
                    </span>
                    <Button size="md" variant="outline" onClick={() => toggleTargets(id)}>
                      Close
                    </Button>
                  </div>

                  <BuyTargetPanel
                    itemId={id}
                    reference={row.reference}
                    entry={targetsByItemId?.get(id)}
                    onChanged={onChanged}
                    // No target yet → the prices first, since they are what
                    // the target gets set against (Guillaume, 2026-10-12).
                    initialTab={
                      matchItemId === id || !targetsByItemId?.get(id)?.targets?.length
                        ? "matches"
                        : "target"
                    }
                  />
                </div>
              </li>
            )}
          </React.Fragment>
        );
      })}
    </ul>
    </SortableContext>
    </DndContext>
  );
};

export default CoreListTable;
